import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid
} from '@mui/material';
import {
  Send as SendIcon,
  Email as EmailIcon,
  Group as GroupIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useGroups } from '../../contexts/GroupContext';
import { useInvitations } from '../../contexts/InvitationContext';
import { format } from 'date-fns';

export default function PartyInvitationManager({ 
  open, 
  onClose, 
  party, 
  onInvitationsSent 
}) {
  const { groups } = useGroups();
  const { sendPartyInvitations } = useInvitations();
  
  const [selectedGroup, setSelectedGroup] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingInvitations, setSendingInvitations] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Helper function to safely format dates
  const safeFormatDate = useCallback((dateValue, formatString) => {
    if (!dateValue) return 'TBD';
    
    try {
      let date;
      
      // Handle Firestore Timestamp
      if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (typeof dateValue === 'number') {
        date = new Date(dateValue);
      } else {
        return 'TBD';
      }
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'TBD';
      }
      
      return format(date, formatString);
    } catch (error) {
      console.warn('PartyInvitationManager: Error formatting date:', error);
      return 'TBD';
    }
  }, []);

  // Generate default email message
  const generateDefaultMessage = useCallback((partyData) => {
    const date = safeFormatDate(partyData.date, 'EEEE, MMMM do, yyyy');
    return `Hi there!

You're invited to join us for ${partyData.name}!

📅 Date: ${date}
📍 Location: ${partyData.location}
👥 Group: ${partyData.groupName}

${partyData.notes ? `📝 Details: ${partyData.notes}\n\n` : ''}Please RSVP to let us know if you can make it!

Looking forward to celebrating with you! 🎉`;
  }, [safeFormatDate]);

  // Initialize form when party changes
  useEffect(() => {
    if (party && open) {
      setSelectedGroup(party.groupId || '');
      setEmailSubject(`You're invited to ${party.name}!`);
      setEmailMessage(generateDefaultMessage(party));
      setError('');
      setSuccess('');
    }
  }, [party, open, generateDefaultMessage]);

  // Get current group members
  const currentGroup = groups.find(g => g.id === selectedGroup);
  const groupMembers = currentGroup?.members || [];

  // Send invitations
  const handleSendInvitations = async () => {
    if (!selectedGroup) {
      setError('Please select a group.');
      return;
    }

    if (!emailSubject.trim() || !emailMessage.trim()) {
      setError('Please provide both email subject and message.');
      return;
    }

    setSendingInvitations(true);
    setError('');
    setSuccess('');

    try {
      const partyData = {
        ...party,
        emailSubject: emailSubject.trim(),
        emailMessage: emailMessage.trim()
      };

      // Send invitations to ALL group members
      await sendPartyInvitations(party.id, partyData, groupMembers);
      
      setSuccess(`Successfully sent ${groupMembers.length} invitation(s) to all group members!`);
      
      // Call callback if provided
      if (onInvitationsSent) {
        onInvitationsSent(groupMembers.length);
      }

      // Close dialog after a short delay
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Error sending invitations:', error);
      setError(error.message || 'Failed to send invitations. Please try again.');
    } finally {
      setSendingInvitations(false);
    }
  };

  const handleClose = () => {
    if (!sendingInvitations) {
      onClose();
    }
  };

  const isFormValid = selectedGroup && emailSubject.trim() && emailMessage.trim();

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon color="primary" />
          Send Party Invitations
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Party Info */}
        {party && (
          <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              {party.name}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  📅 {safeFormatDate(party.date, 'MMM dd, yyyy')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  📍 {party.location}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Group Selection */}
        <FormControl fullWidth margin="normal" disabled={sendingInvitations}>
          <InputLabel>Select Group</InputLabel>
          <Select
            value={selectedGroup}
            label="Select Group"
            onChange={(e) => setSelectedGroup(e.target.value)}
            required
          >
            {groups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon color="action" />
                  <Box>
                    <Typography variant="body2">{group.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {group.memberCount || group.members?.length || 0} members
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Group Member Information */}
        {selectedGroup && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: 'info.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="info.main" gutterBottom>
              <GroupIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Group Members
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Invitations will be sent to all {groupMembers.length} members of "{currentGroup?.name}".
              Each member will receive a personalized email invitation.
            </Typography>
          </Box>
        )}

        {/* Email Customization */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Email Customization
          </Typography>
          
          <TextField
            fullWidth
            label="Email Subject"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            margin="normal"
            required
            disabled={sendingInvitations}
            helperText="Subject line for the invitation email"
          />

          <TextField
            fullWidth
            label="Email Message"
            value={emailMessage}
            onChange={(e) => setEmailMessage(e.target.value)}
            margin="normal"
            required
            disabled={sendingInvitations}
            multiline
            rows={6}
            helperText="Personalize your invitation message"
          />
        </Box>

        {/* Invitation Summary */}
        {selectedGroup && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: 'primary.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              <InfoIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Invitation Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You're about to send {groupMembers.length} invitation(s) to all members of "{currentGroup?.name}".
              Each member will receive a personalized email invitation.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={handleClose} 
          disabled={sendingInvitations}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSendInvitations}
          variant="contained"
          disabled={!isFormValid || sendingInvitations}
          startIcon={sendingInvitations ? <CircularProgress size={16} /> : <SendIcon />}
        >
          {sendingInvitations ? 'Sending...' : `Send Invitations to All Group Members`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
